/**
 * Labstep
 *
 * @module containers/AuthenticatedUser
 * @desc Containers that gives access to the logged user
 */

import { readAuthenticatedUser } from 'labstep-web/state/actions/user';
import {
  selectAuthenticatedUser,
  selectAuthenticatedUserEntity,
  selectAuthenticatedUserLoadStatus,
  selectLoginStatus,
} from 'labstep-web/state/selectors/authenticated-user';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  IWithAuthenticatedUserInjectedProps,
  WithAuthenticatedUserEntityInjectedProps,
} from './types';

/**
 * Modern hooks-based authenticated user HOC
 */
export const withAuthenticatedUser = <P, S>(
  Component: React.FC<P> | React.ComponentClass<P, S>,
): React.FC<
  Pick<P, Exclude<keyof P, keyof IWithAuthenticatedUserInjectedProps>>
> => {
  const WrappedComponent: React.FC<
    Pick<
      P,
      Exclude<keyof P, keyof IWithAuthenticatedUserInjectedProps>
    >
  > = React.memo((props) => {
    const authenticatedUser = useSelector(selectAuthenticatedUser);
    const loggingStatus = useSelector(selectLoginStatus);

    const injectedProps = useMemo(
      () => ({
        authenticatedUser,
        loggingStatus,
      }),
      [authenticatedUser, loggingStatus],
    );

    return <Component {...(props as P)} {...injectedProps} />;
  });

  WrappedComponent.displayName = `withAuthenticatedUser(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

/**
 * Modern hooks-based authenticated user entity HOC
 */
export const withAuthenticatedUserEntity = <P, S>(
  Component: React.FC<P> | React.ComponentClass<P, S>,
): React.FC<
  Pick<
    P,
    Exclude<keyof P, keyof WithAuthenticatedUserEntityInjectedProps>
  >
> => {
  const WrappedComponent: React.FC<
    Pick<
      P,
      Exclude<keyof P, keyof WithAuthenticatedUserEntityInjectedProps>
    >
  > = React.memo((props) => {
    const dispatch = useDispatch();
    const authenticatedUserEntity = useSelector(
      selectAuthenticatedUserEntity,
    );
    const authenticatedUserEntityStatus = useSelector(
      selectAuthenticatedUserLoadStatus,
    );

    const read = useCallback(
      (options: any) => dispatch(readAuthenticatedUser(options)),
      [dispatch],
    );

    const injectedProps = useMemo(
      () => ({
        authenticatedUserEntity,
        authenticatedUserEntityStatus,
        read,
      }),
      [authenticatedUserEntity, authenticatedUserEntityStatus, read],
    );

    return <Component {...(props as P)} {...injectedProps} />;
  });

  WrappedComponent.displayName = `withAuthenticatedUserEntity(${Component.displayName || Component.name})`;

  return WrappedComponent;
};
